import React, { useState, useEffect } from 'react';
import './App.css';
import { LayoutDashboard, Beef, BarChart2, FileText, LogOut } from 'lucide-react';
import Dashboard from './pages/Dashboard';
import AnimalsList from './pages/AnimalsList';
import AnimalDetail from './pages/AnimalDetail';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import Login from './pages/Login';

type Page = 'dashboard' | 'animals' | 'animal-detail' | 'analytics' | 'reports';

export default function App() {
  const [page, setPage] = useState<Page>('dashboard');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const saved = sessionStorage.getItem('agrosmart_token');
    if (saved) {
      fetch('/api/auth/verify/', {
        headers: { 'Authorization': `Token ${saved}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => { setToken(saved); setUser(data.user); setChecking(false); })
        .catch(() => { sessionStorage.removeItem('agrosmart_token'); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, []);

  const handleLogin = (t: string, u: any) => {
    sessionStorage.setItem('agrosmart_token', t);
    setToken(t);
    setUser(u);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('agrosmart_token');
    setToken(null);
    setUser(null);
  };

  if (checking) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner" /></div>;
  }

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  const navigate = (p: string, id?: number) => {
    setPage(p as Page);
    if (id !== undefined) setSelectedId(id);
  };

  const navItems = [
    { key: 'dashboard', label: 'Dashboard',       icon: <LayoutDashboard size={18} /> },
    { key: 'animals',   label: 'Chorvalar',        icon: <Beef size={18} /> },
    { key: 'analytics', label: 'Tahlil',           icon: <BarChart2 size={18} /> },
    { key: 'reports',   label: 'Hisobotlar',       icon: <FileText size={18} /> },
  ];

  const titles: Record<string, string> = {
    dashboard: 'Bosh sahifa',
    animals: "Chorvalar ro'yxati",
    'animal-detail': "Chorva ma'lumotlari",
    analytics: 'Tahlil va statistika',
    reports: 'Hisobotlar',
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>🌱 AgroSmart</h1>
          <span>Chorvachilik platformasi</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button key={item.key}
              className={`nav-item ${(page === item.key || (page === 'animal-detail' && item.key === 'animals')) ? 'active' : ''}`}
              onClick={() => navigate(item.key)}>
              {item.icon}{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px' }}>
            <span style={{ fontSize: 12 }}>{user?.first_name || user?.username}</span>
            <button onClick={handleLogout} title="Chiqish"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <div className="page-header">
          <h2>{titles[page] || ''}</h2>
          <div style={{ fontSize: 13, color: 'var(--gray)' }}>
            {new Date().toLocaleDateString('uz-UZ', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {page === 'dashboard'     && <Dashboard    onNavigate={navigate} />}
        {page === 'animals'       && <AnimalsList  onNavigate={navigate} />}
        {page === 'animal-detail' && selectedId !== null && <AnimalDetail animalId={selectedId} onBack={() => navigate('animals')} />}
        {page === 'analytics'     && <Analytics />}
        {page === 'reports'       && <Reports onNavigate={navigate} />}
      </main>
    </div>
  );
}
